// @ts-nocheck
// Note: this is the only functional component in LandingPage2 — most blocks here use
// the class component + *Controller.web.tsx pattern. This nav is presentational only,
// so hooks (useState) keep it self-contained without adding a controller.
// History is injected via withRouter (react-router-dom 4.3 predates useHistory).
import React, { useState } from "react";
import {
  Button,
  Collapse,
  Divider,
  Drawer,
  Grid,
  Hidden,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ChevronDown, ChevronUp } from "lucide-react";
import { NavLink, withRouter } from "react-router-dom";
import { RouterProps } from "react-router";

import { logo } from "../assets";
import { useSubscription } from "../../../../framework/src/contexts/Subscription";
import {
  NavLinkConfig,
  SIDEBAR_LINKS,
  SubLinkConfig,
  isExternalLink,
} from "./navLinks";
import { useLateralNavStyles } from "./styles";

type LateralNavOwnProps = {
  mobileOpen: boolean;
  onMobileToggle: () => void;
  onCreateNewRoadmap: () => void;
};

type LateralNavProps = LateralNavOwnProps & RouterProps;

const LateralNav: React.FC<LateralNavProps> = ({
  mobileOpen,
  onMobileToggle,
  onCreateNewRoadmap,
  history,
}) => {
  const classes = useLateralNavStyles();
  const [activeSideLink, setActiveSideLink] = useState<string>(
    history.location.pathname
  );
  const initialExpanded =
    SIDEBAR_LINKS.find(
      (link) =>
        link.subLinks &&
        link.subLinks.length > 0 &&
        history.location.pathname.includes(link.url)
    )?.url ?? null;
  const [expandedSection, setExpandedSection] = useState<string | null>(
    initialExpanded
  );

  const toggleSection = (url: string) => {
    setExpandedSection((current) => (current === url ? null : url));
  };
  const { subscriptionLoading, subscriptionError, subscribed, trialDaysRemaining } =
    useSubscription();

  const handleTopLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    archive: NavLinkConfig
  ) => {
    localStorage.removeItem("save&next");
    setActiveSideLink(archive.url);
    if (isExternalLink(archive)) {
      event.preventDefault();
      window.open(archive.url, "_blank", "noopener");
    }
  };

  const handleSubLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    subLink: SubLinkConfig
  ) => {
    localStorage.removeItem("save&next");
    if (subLink.url === "/home/roadmaps/createRoadMap") {
      event.preventDefault();
      onCreateNewRoadmap();
    }
  };

  const renderSubLink = (subLink: SubLinkConfig) => (
    <Grid item xs={12} key={subLink.title}>
      <NavLink
        to={subLink.url}
        onClick={(event) => handleSubLinkClick(event, subLink)}
        className={classes.subLink}
        activeClassName={classes.activeSubLink}
      >
        {subLink.title}
      </NavLink>
    </Grid>
  );

  const renderNavLink = (archive: NavLinkConfig) => {
    const hasSubLinks = !!archive.subLinks && archive.subLinks.length > 0;
    const Icon = archive.icon;

    if (hasSubLinks) {
      const isExpanded = expandedSection === archive.url;
      const isActive = history.location.pathname.includes(archive.url);
      return (
        <React.Fragment key={archive.title}>
          <Grid
            item
            xs={12}
            md={12}
            className={classes.sideStyleBar}
          >
            <div
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              className={`${classes.sideLink} ${classes.navRowToggle} ${
                isActive ? classes.activeSideLink : ""
              }`}
              onClick={() => toggleSection(archive.url)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  toggleSection(archive.url);
                }
              }}
            >
              <div className={classes.navRowLabel}>
                {Icon && <Icon size={18} />}
                <span>{archive.title}</span>
              </div>
              {isExpanded ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )}
            </div>
          </Grid>
          <Collapse
            timeout={300}
            mountOnEnter
            unmountOnExit
            in={isExpanded}
            style={{ width: "100%" }}
          >
            {archive.subLinks.map(renderSubLink)}
          </Collapse>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={archive.title}>
        <Grid
          item
          xs={12}
          md={12}
          className={classes.sideStyleBar}
        >
          <NavLink
            to={archive.url}
            className={classes.sideLink}
            activeClassName={classes.activeSideLink}
            id={archive.id}
            onClick={(event) => handleTopLinkClick(event, archive)}
          >
            {Icon && <Icon size={18} />}
            <span>{archive.title}</span>
          </NavLink>
        </Grid>
      </React.Fragment>
    );
  };

  const renderTrialBanner = () =>
    !subscriptionLoading &&
    !subscriptionError &&
    !subscribed &&
    trialDaysRemaining > 0 ? (
      <Grid item xs={12} className={classes.trialPeriodBoxContainer}>
        <Grid
          container
          direction="column"
          className={classes.trialPeriodBox}
        >
          <Typography className={classes.trialTitle}>
            {trialDaysRemaining} days left
          </Typography>
          <Typography className={classes.trialSubtitle}>
            in Trial Period
          </Typography>
        </Grid>
      </Grid>
    ) : null;

  const renderDesktopSidebar = () => (
    <Grid item className={classes.sideBar}>
      <Grid
        container
        justifyContent="center"
        className={classes.sideBarContain}
      >
        <Grid item md={8}>
          <img src={logo} alt="logo" style={{ width: "100%" }} />
        </Grid>
        <Grid item xs={12}>
          <Divider className={classes.logoDivider} />
        </Grid>
        <Grid item xs={12} md={12} className={classes.sideBarLinksContainer}>
          <Grid container justifyContent="center" alignItems="center">
            {SIDEBAR_LINKS.map(renderNavLink)}
          </Grid>
        </Grid>
        {renderTrialBanner()}
      </Grid>
    </Grid>
  );

  const renderMobileDrawer = () => (
    <Drawer
      variant="temporary"
      anchor="left"
      open={mobileOpen}
      onClose={onMobileToggle}
      classes={{ paper: classes.drawerPaper }}
      ModalProps={{ keepMounted: true }}
    >
      <IconButton onClick={onMobileToggle} className={classes.closeMenuButton}>
        <CloseIcon />
      </IconButton>
      <Grid
        container
        justifyContent="space-around"
        className={classes.sideBarContain}
      >
        <Grid item xs={12} md={9}>
          <Grid container justifyContent="space-around">
            {SIDEBAR_LINKS.map(renderNavLink)}
          </Grid>
        </Grid>
      </Grid>
      <Divider variant="middle" />
      <Grid container justifyContent="center" alignItems="center">
        <Grid item md={6}>
          <Button
            variant="text"
            color="primary"
            onClick={() => {
              history.push("/");
            }}
          >
            Back to Home
          </Button>
        </Grid>
      </Grid>
    </Drawer>
  );

  return (
    <>
      <Hidden smDown>{renderDesktopSidebar()}</Hidden>
      <Hidden smUp implementation="css">
        {renderMobileDrawer()}
      </Hidden>
    </>
  );
};

export default withRouter(LateralNav);
